import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useParentEntityContext } from 'src/contexts/ParentEntityContext';
import ReactPlayer from 'react-player/lazy';
import { useAddContentProgressMutation } from 'src/redux/slices/api/su';
import { InstanceStateData } from 'src/types/state/learningModule';
import { useClientId } from 'src/contexts/ClientContext';
import { extractRefTag, extractReferral } from 'src/utils/refParameterConversion';
import Logger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';

export interface VideoPlayerProps {
  videoUrl: string;
  learningModuleInstance: InstanceStateData;
  clientId?: string;
}

const PROGRESS_MILESTONES = [0.25, 0.5, 0.75, 1.0]; // 25%, 50%, 75%, 100%

const VideoPlayer = ({ videoUrl, learningModuleInstance, clientId: propClientId }: VideoPlayerProps) => {
  const reactPlayerRef = useRef<ReactPlayer>(null);
  const [addContentProgress] = useAddContentProgressMutation();
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [reachedMilestones, setReachedMilestones] = useState<Set<number>>(new Set());
  const { parentId, parentType } = useParentEntityContext();
  const contextClientId = useClientId();
  const [searchParams] = useSearchParams();

  // Use provided clientId if available, otherwise use the one from context
  const clientId = propClientId ?? contextClientId;
  // Base addContentProgress API trigger logic
  const triggerAddContentProgress = useCallback(() => {
    const videoPlayer = reactPlayerRef.current;
    if (videoPlayer) {
      const currentTime = videoPlayer.getCurrentTime();
      const duration = videoPlayer.getDuration();

      // Skip if duration is not valid - prevents sending null values to backend
      if (!duration || Number.isNaN(duration) || duration <= 0) {
        return;
      }

      const refTag = extractRefTag(searchParams);
      const referral = extractReferral(searchParams);
      addContentProgress({
        instance: learningModuleInstance,
        createdAt: Date.now().valueOf(),
        progress: {
          lastWatchedPosition: currentTime || 0,
          contentLength: duration,
        },
        clientId,
        refTag,
        referral,
        parentId,
        parentType,
      });
    }
  }, [searchParams, learningModuleInstance, addContentProgress, clientId, parentId, parentType]);

  // Check if current progress has reached a new milestone
  const checkMilestone = useCallback(() => {
    const videoPlayer = reactPlayerRef.current;
    if (videoPlayer) {
      const currentTime = videoPlayer.getCurrentTime();
      const duration = videoPlayer.getDuration();

      // Skip if duration or currentTime is not valid
      if (!duration || Number.isNaN(duration) || duration <= 0
          || (!currentTime && currentTime !== 0) || Number.isNaN(currentTime)) {
        return;
      }

      const progress = currentTime / duration;

      // Find the highest milestone reached that hasn't been reported yet
      const newMilestone = PROGRESS_MILESTONES.find(
        (milestone) => progress >= milestone && !reachedMilestones.has(milestone),
      );

      if (newMilestone) {
        setReachedMilestones((prev) => new Set(prev).add(newMilestone));
        triggerAddContentProgress();
      }
    }
  }, [reachedMilestones, triggerAddContentProgress]);

  // Content Progress Handlers
  const handleContentProgress = () => {
    checkMilestone();
  };

  const handleContentProgressImmediate = () => {
    triggerAddContentProgress();
  };

  // Video Player Event Handlers
  const onStart = () => {
    handleContentProgressImmediate(); // Always call on first play
    setIsPlaying(true);
  };
  const onPlay = () => {
    setIsPlaying(true);
  };
  const onEnded = () => {
    handleContentProgress();
    setIsPlaying(false);
  };
  const onPause = () => {
    setIsPlaying(false);
    handleContentProgress(); // Check milestone before calling
  };
  const onProgress = () => {
    if (isPlaying) {
      handleContentProgress(); // Check milestones during playback
    }
  };
  const onError = (error: unknown) => {
    Logger.error(ClientLog.VideoPlaybackError, new Error(`Video playback failed: ${videoUrl}`), {
      videoUrl,
      moduleId: learningModuleInstance.moduleId,
      originalError: String(error),
    });
  };

  // Reset milestones on video url change
  useEffect(() => {
    setIsPlaying(false);
    setReachedMilestones(new Set());
  }, [videoUrl]);

  return (
    <div className="relative pt-[56.25%]">
      <ReactPlayer
        ref={reactPlayerRef}
        url={videoUrl}
        playing={isPlaying}
        className="absolute left-su-space-00 top-su-space-00"
        controls
        width="100%"
        height="100%"
        onPlay={onPlay}
        onPause={onPause}
        onStart={onStart}
        onEnded={onEnded}
        onProgress={onProgress}
        onError={onError}
      />
    </div>
  );
};

export default VideoPlayer;
